import { FC, useContext, useEffect, useState } from 'react';
import { DocumentRequest, DocumentRequestStatus, DocumentRequestUser } from '@/tsClient/servers/noodleDocuments/document-api-models';
import Buttons from '@components/Buttons';
import { useRouter } from 'next/router';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import FileDashed from '@components/Icons/FileDashed';
import Clock from '@components/Icons/Clock';
import Integration from '@components/Icons/Integration';
import File from '@components/Icons/File';
import CheckCircle from '@components/Icons/CheckCircle';
import CaretRight from '@components/Icons/CaretRight';
import WarningCircle from '@components/Icons/WarningCircle';
import DashboardContext from '@layouts/DashboardLayout/DashboardContext';
import { useOpenDocumentRequestUserPane } from '@panes/hooks';
import UserProfileContext from '@layouts/UserProfileLayout/UserProfileContext';
import * as format from '@format';
import SkipTaskConfirmationModal from '@/modals/SkipTaskConfirmationModal';
import { MESSAGE_ATTACHMENT_REFERENCE_TYPES } from '@/typings/api-models';
import { updateDocumentRequestUser } from '@/tsClient/documentRequests';
import Tooltip from '../Tooltip';
import s from './UserDocumentRequestCard.module.scss';

type Props = {
  request: Pick<DocumentRequestUser, 'id' | 'status'> & {
    documentRequest: Pick<DocumentRequest, 'id' | 'title' | 'isForCreator'>;
    actionRequiredFiles: number;
    uploadedRequiredFiles: number;
    totalRequiredFiles: number;
    uploadedFiles: number;
    totalFiles: number;
  }
  ;
  canClick: boolean;
  canSkip?:boolean;
  creatorSlug?: string;
  setIsFulfilled?: (f: boolean) => void;
  setInReview?: (f: boolean) => void;
  setIsSkipped?: (f: boolean) => void;
  completedAt?: string | null;
  integratedWith?: 'dol' | 'dos' | 'pacer' | 'uscis';
  reload?: () => Promise<void>;
};

const UserDocumentRequestCard: FC<Props> = ({
  setIsFulfilled,
  setInReview,
  setIsSkipped,
  creatorSlug,
  request,
  canClick,
  canSkip,
  completedAt,
  integratedWith,
  reload,
}) => {
  const router = useRouter();
  const { creator, isInDashboard } = useContext(DashboardContext);
  const { isInUserProfile } = useContext(UserProfileContext);
  const openDocumentRequestUserPane = useOpenDocumentRequestUserPane();
  const [skipTaskModalOpen, setSkipTaskModalOpen] = useState(false);

  const handleClick = (): void => {
    if (isInDashboard || isInUserProfile) {
      openDocumentRequestUserPane({ documentRequestUserId: request.id });
    } else {
      router.push(
        getUrl(IDENTIFIERS.DOCUMENT_REQUEST_USER_DETAIL, {
          creatorSlug: creatorSlug || creator?.slug || (router.query.creatorSlug as string) || '',
          documentRequestUserId: request.id,
        }),
      );
    }
  };

  const handleSkipTask = async (): Promise<void> => {
    await updateDocumentRequestUser({id:request.id, status: DocumentRequestStatus.SKIPPED, userWorkflowId: null});
    setSkipTaskModalOpen(false);
    if(reload) reload();
  };

  const isAllRequiredUploaded = request.uploadedRequiredFiles === request.totalRequiredFiles;
  const noRequiredFiles = request.totalRequiredFiles === 0;

  const { isForCreator } = request.documentRequest;
  const isUnderReview = isAllRequiredUploaded && !noRequiredFiles && request.status === DocumentRequestStatus.IN_REVIEW;
  const isActionRequired = request.actionRequiredFiles > 0;
  const isCompleted = isAllRequiredUploaded && !noRequiredFiles && request.status === DocumentRequestStatus.SUCCEEDED;
  const isSkipped = request.status === DocumentRequestStatus.SKIPPED;

  useEffect(() => {
    setIsFulfilled?.(isCompleted);
  }, [isCompleted, setIsFulfilled]);

  useEffect(() => {
    setInReview?.(isUnderReview);
  }, [isUnderReview, setInReview]);

  useEffect(() => {
    setIsSkipped?.(isSkipped);
  }, [isSkipped, setIsSkipped]);

  return (
    <>
      {skipTaskModalOpen && (
        <SkipTaskConfirmationModal
          onClose={() => setSkipTaskModalOpen(false)}
          onConfirm={handleSkipTask}
          taskType={MESSAGE_ATTACHMENT_REFERENCE_TYPES.DOCUMENT_REQUEST_USER}
        />
      )}
      <Buttons className={s.container} key={request.id} isFullWidth isWrapper onClick={handleClick} disabled={!canClick || (isSkipped && !canSkip)}
        style={isSkipped ? { backgroundColor: 'var(--color-gray-25)', borderRadius: 8 } : {}}>
        {isCompleted
          ? <CheckCircle size={16} weight="fill" color="var(--color-success)" />
          : <figure>
            {!noRequiredFiles && (
              <svg className={s.progress} width={36} height={36} viewBox="0 0 46 46">
                <circle
                  cx={23}
                  cy={23}
                  r={22}
                  style={{
                    strokeDasharray: '138',
                    strokeDashoffset: `${((request.totalRequiredFiles - request.uploadedRequiredFiles) / request.totalRequiredFiles) * 138}`,
                  }}
                />
              </svg>
            )}
            {isAllRequiredUploaded && !noRequiredFiles && request.status === DocumentRequestStatus.SUCCEEDED
              ? (
                <File weight="fill" size={20} color="var(--color-gray-0)" />
              )
              : (
                <FileDashed size={20} color="var(--color-gray-100)" />
              )}
            {isAllRequiredUploaded && !noRequiredFiles && request.status === DocumentRequestStatus.SUCCEEDED && (
              <div className={s.complete}>
                <CheckCircle size={16} weight="fill" color="var(--color-success)" />
              </div>
            )}
            {isUnderReview && !noRequiredFiles && isAllRequiredUploaded && (
              <div className={s.complete}>
                <Clock size={16} weight="fill" color="var(--color-gray-100)" />
              </div>
            )}
            {isActionRequired && (
              <div className={s.complete}>
                <WarningCircle size={16} weight="fill" color="var(--color-warning)" />
              </div>
            )}
          </figure>}
        <div>
          <div className={s.type}>
            {integratedWith && (
              <Tooltip text={`Data in this document request is going to eFile with the ${integratedWith.toUpperCase()}`}>
                <Integration weight="fill" color="var(--color-noodle)" size={12} />
              </Tooltip>
            )}
          </div>
          <p
            className={isCompleted ? "caption-bold" : "body-sm-bold"}
            style={isCompleted ? { color: 'var(--color-gray-75)' } : { lineHeight: 1.4 }}
          >
            {request.documentRequest?.title}
          </p>
          {noRequiredFiles
            ? (
              <p className="caption">
                {request.totalFiles === 0
                  ? (
                    <>
                      No documents yet
                    </>
                  )
                  : (
                    <>
                      {request.totalFiles} optional document{request.totalFiles > 1 ? 's' : ''}{' '}
                      {isUnderReview && request.uploadedFiles > 0 ? 'under review' : ''}
                    </>)}
              </p>
            )
            : (
              !isSkipped && <p className="caption">
                {isUnderReview && creator && !isForCreator && (
                  <>
                    <strong style={{ color: 'var(--color-warning)' }}>Review required by you</strong> ·{' '}
                  </>
                )}
                {(isAllRequiredUploaded && !noRequiredFiles && request.status === DocumentRequestStatus.IN_PROGRESS
                && !isActionRequired && !creator && (
                  <>
                All required documents uploaded.{!isForCreator && <strong style={{ color: 'var(--color-warning)' }}>Please submit for review</strong>}
                  </>
                )) || (
                  <>
                    {!isCompleted && <>
                      {!isUnderReview && `${request.uploadedRequiredFiles} of `}
                      {request.totalRequiredFiles} required document{request.totalRequiredFiles > 1 ? 's' : ''}{' '}
                      {isUnderReview && request.uploadedFiles > 0 ? 'under review' : 'uploaded'}
                    </>}
                    {completedAt && isCompleted && `Completed on ${format.datetimeLocalTZ.withoutTimezone({ datetime: completedAt })}`}
                  </>
                )}
              </p>
            )}
          {isSkipped && <p className="caption">This task is no longer required.</p>}
        </div>

        {canClick && (
          <>
            {isCompleted
              ? <CaretRight size={12} color="var(--color-gray-100)" />
              : <div
                className={s.open}
                {...(((request.status !== DocumentRequestStatus.SUCCEEDED && !creator) || (isUnderReview && creator)) && {
                  style: {
                    backgroundColor: 'var(--color-primary)',
                    border: '1px solid var(--color-primary)',
                    color: 'var(--color-gray-0)',
                  },
                })}
              >
              View
              </div>}
          </>
        )}
      </Buttons>
      {canSkip && !isSkipped && (
        <Buttons
          onClick={(event) => {
            event.stopPropagation();
            setSkipTaskModalOpen(true);
          }}
          className={s.skip}
        >
    Skip
        </Buttons>
      )}
    </>
  );
};

export default UserDocumentRequestCard;
